import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/*
  InvoiceEditDialog · Iter 85.66
  ──────────────────────────────
  Editable form for a Draft invoice. Line items are editable inline
  (description, hours, rate — Amount recomputes locally as `hours * rate`
  for immediate feedback; the server re-runs the same math authoritatively
  on Save so the totals row can't drift). Sent/Paid invoices never reach
  this dialog — the parent only opens it for `status === 'draft'`.

  Contract:
    · Save PUTs a single payload to `/invoices/{id}` — no separate line-
      item endpoint, so the invoice number and provenance stay stable and
      no duplicate row is ever created.
    · On success, the parent reloads its list and the QuickView reads
      from the fresh canonical row.
*/
const emptyLine = { description: "", hours: 0, rate: 0, amount: 0 };

export default function InvoiceEditDialog({ invoice, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !invoice) return;
    setForm({
      period_start: invoice.period_start || "",
      period_end: invoice.period_end || "",
      issue_date: invoice.issue_date ? String(invoice.issue_date).slice(0, 10) : "",
      due_date: invoice.due_date ? String(invoice.due_date).slice(0, 10) : "",
      notes: invoice.notes || "",
      line_items: (invoice.line_items || []).map((li) => ({
        description: li.description || li.title || "",
        hours: Number(li.hours ?? li.quantity ?? 0),
        rate: Number(li.rate ?? 0),
        amount: Number(li.amount ?? 0),
      })),
    });
  }, [open, invoice]);

  if (!invoice || !form) return null;

  const setLine = (i, patch) => {
    const next = [...form.line_items];
    const merged = { ...next[i], ...patch };
    const hours = Number(merged.hours || 0);
    const rate = Number(merged.rate || 0);
    merged.amount = Number((hours * rate).toFixed(2));
    next[i] = merged;
    setForm({ ...form, line_items: next });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { ...emptyLine }] });
  const removeLine = (i) => {
    const next = [...form.line_items];
    next.splice(i, 1);
    setForm({ ...form, line_items: next });
  };

  const subtotal = form.line_items.reduce((s, li) => s + Number(li.amount || 0), 0);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // Server normalizes hours/rate/amount and recomputes subtotal + tax
      // + total from the canonical firm tax rate.
      const { data } = await api.put(`/invoices/${invoice.id}`, {
        period_start: form.period_start,
        period_end: form.period_end,
        issue_date: form.issue_date,
        due_date: form.due_date,
        notes: form.notes,
        line_items: form.line_items,
      });
      toast.success("Invoice updated");
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="invoice-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit invoice · {invoice.invoice_number}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Period start</Label>
              <Input
                type="date"
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                data-testid="invoice-edit-period-start"
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Period end</Label>
              <Input
                type="date"
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                data-testid="invoice-edit-period-end"
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Issue date</Label>
              <Input
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                data-testid="invoice-edit-issue"
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Due date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="invoice-edit-due"
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Line items</Label>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="invoice-edit-add-line" className="rounded-none">
                <Plus size={11} weight="bold" className="mr-1" /> Add line
              </Button>
            </div>
            <div className="border border-border">
              <div className="grid grid-cols-[1fr_90px_100px_110px_32px] gap-2 px-3 py-2 border-b border-border bg-secondary/40 text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
                <div>Description</div>
                <div className="text-right">Hours</div>
                <div className="text-right">Rate</div>
                <div className="text-right">Amount</div>
                <div></div>
              </div>
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-[1fr_90px_100px_110px_32px] gap-2 px-3 py-2 border-b border-border/50 last:border-b-0 items-center" data-testid={`invoice-edit-row-${i}`}>
                  <Input
                    value={li.description}
                    onChange={(e) => setLine(i, { description: e.target.value })}
                    className="rounded-none bg-transparent border-0 border-b border-transparent focus-visible:border-primary focus-visible:ring-0 h-7 text-sm px-1"
                    data-testid={`invoice-edit-desc-${i}`}
                  />
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    value={li.hours}
                    onChange={(e) => setLine(i, { hours: Number(e.target.value) || 0 })}
                    className="rounded-none bg-transparent border-0 focus-visible:ring-0 h-7 text-xs text-right font-mono px-1"
                    data-testid={`invoice-edit-hours-${i}`}
                  />
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={li.rate}
                    onChange={(e) => setLine(i, { rate: Number(e.target.value) || 0 })}
                    className="rounded-none bg-transparent border-0 focus-visible:ring-0 h-7 text-xs text-right font-mono px-1"
                    data-testid={`invoice-edit-rate-${i}`}
                  />
                  <div className="text-right font-mono text-xs tabular-nums text-primary" data-testid={`invoice-edit-amount-${i}`}>
                    ${Number(li.amount || 0).toFixed(2)}
                  </div>
                  <button
                    onClick={() => removeLine(i)}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove line ${i + 1}`}
                    data-testid={`invoice-edit-remove-${i}`}
                  >
                    <Trash size={13} weight="bold" />
                  </button>
                </div>
              ))}
              {form.line_items.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-muted-foreground">No line items — add one to get started.</div>
              )}
              <div className="grid grid-cols-[1fr_200px] gap-2 px-3 py-2 border-t border-border bg-secondary/20 text-xs">
                <div className="text-right font-mono uppercase tracking-widest text-muted-foreground">Subtotal (recalc on save)</div>
                <div className="text-right font-mono text-primary">${subtotal.toFixed(2)}</div>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="invoice-edit-notes"
              className="rounded-none bg-secondary/40 mt-1"
              placeholder="Payment terms, references, gratitude…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="invoice-edit-save"
            onClick={save}
            disabled={saving}
            className="rounded-none bg-primary text-primary-foreground"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
